import React, { useState, useEffect } from 'react'
import { Search, Check, X, Eye, Store, Mail, Phone } from 'lucide-react'
import axios from 'axios'
import { toast, ToastContainer } from 'react-toastify'
import 'react-toastify/dist/ReactToastify.css'

const SellersPage = () => {
  const [sellers, setSellers] = useState([])
  const [loading, setLoading] = useState(true)
  const [searchTerm, setSearchTerm] = useState("")
  const [statusFilter, setStatusFilter] = useState("All")

  // Demo data for now (you'll replace with API call)
  const demoSellers = [
    { id: 1, name: "Green Gardens Nursery", email: "contact@greengardens.com", phone: "+91 98765 43210", products: 45, sales: 128500, status: "Approved", joinDate: "2023-06-15" },
    { id: 2, name: "Plant Paradise", email: "info@plantparadise.com", phone: "+91 98765 43211", products: 32, sales: 95600, status: "Approved", joinDate: "2023-07-20" },
    { id: 3, name: "Nature's Touch", email: "hello@naturestouch.com", phone: "+91 98765 43212", products: 28, sales: 72300, status: "Pending", joinDate: "2024-01-10" },
    { id: 4, name: "Urban Jungle", email: "contact@urbanjungle.com", phone: "+91 98765 43213", products: 56, sales: 185400, status: "Approved", joinDate: "2023-05-08" },
    { id: 5, name: "Bloom & Grow", email: "info@bloomgrow.com", phone: "+91 98765 43214", products: 0, sales: 0, status: "Pending", joinDate: "2024-01-14" },
  ]

  useEffect(() => {
    // For now, use demo data
    setSellers(demoSellers)
    setLoading(false)
    
    // Later replace with actual API call:
    // fetchSellers()
  }, [])

  const fetchSellers = async () => {
    try {
      const token = localStorage.getItem('token')
      const response = await axios.get('http://localhost:5000/api/admin/sellers', {
        headers: { Authorization: `Bearer ${token}` }
      })
      setSellers(response.data)
      setLoading(false)
    } catch (error) {
      console.error('Error fetching sellers:', error)
      toast.error('Failed to fetch sellers')
      setLoading(false)
    }
  }

  const handleApprove = async (sellerId) => {
    try {
      // Update UI immediately
      setSellers(sellers.map(seller => 
        seller.id === sellerId ? { ...seller, status: "Approved" } : seller
      ))
      toast.success('Seller approved successfully!')
      
      // Later add API call:
      // const token = localStorage.getItem('token')
      // await axios.put(`http://localhost:5000/api/admin/sellers/${sellerId}/approve`, {}, {
      //   headers: { Authorization: `Bearer ${token}` }
      // })
    } catch (error) {
      console.error('Error approving seller:', error)
      toast.error('Failed to approve seller')
    }
  }

  const handleReject = async (sellerId) => {
    try {
      // Update UI immediately
      setSellers(sellers.map(seller => 
        seller.id === sellerId ? { ...seller, status: "Rejected" } : seller
      ))
      toast.success('Seller rejected')
      
      // Later add API call:
      // const token = localStorage.getItem('token')
      // await axios.put(`http://localhost:5000/api/admin/sellers/${sellerId}/reject`, {}, {
      //   headers: { Authorization: `Bearer ${token}` }
      // })
    } catch (error) {
      console.error('Error rejecting seller:', error)
      toast.error('Failed to reject seller')
    }
  }

  const getStatusBadge = (status) => {
    const styles = {
      Approved: "bg-success",
      Pending: "bg-warning",
      Rejected: "bg-danger"
    }
    return (
      <span className={`badge ${styles[status] || 'bg-secondary'} text-white px-2 py-1`}>
        {status}
      </span>
    )
  }

  const filteredSellers = sellers.filter((seller) => {
    const matchesSearch = seller.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      seller.email?.toLowerCase().includes(searchTerm.toLowerCase())
    const matchesStatus = statusFilter === "All" || seller.status === statusFilter
    return matchesSearch && matchesStatus
  })

  if (loading) {
    return (
      <div className="text-center py-5">
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
      </div>
    )
  }

  return (
    <div>
      <ToastContainer position="top-right" autoClose={3000} />
      
      {/* Page Header */}
      <div className="mb-4">
        <h1 className="h2 fw-bold mb-1">Sellers</h1>
        <p className="text-muted">Manage seller accounts and approvals</p>
      </div>

      {/* Stats Cards */}
      <div className="row g-4 mb-4">
        <div className="col-md-3">
          <div className="card shadow-sm">
            <div className="card-body">
              <small className="text-muted">Total Sellers</small>
              <h3 className="mb-0 fw-bold">{sellers.length}</h3>
            </div>
          </div>
        </div>
        <div className="col-md-3">
          <div className="card shadow-sm">
            <div className="card-body">
              <small className="text-muted">Approved</small>
              <h3 className="mb-0 fw-bold text-success">{sellers.filter(s => s.status === "Approved").length}</h3>
            </div>
          </div>
        </div>
        <div className="col-md-3">
          <div className="card shadow-sm">
            <div className="card-body">
              <small className="text-muted">Pending</small>
              <h3 className="mb-0 fw-bold text-warning">{sellers.filter(s => s.status === "Pending").length}</h3>
            </div>
          </div>
        </div>
        <div className="col-md-3">
          <div className="card shadow-sm">
            <div className="card-body">
              <small className="text-muted">Total Sales</small>
              <h3 className="mb-0 fw-bold">₹{(sellers.reduce((sum, s) => sum + (s.sales || 0), 0) / 1000).toFixed(0)}K</h3>
            </div>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="d-flex flex-column flex-sm-row gap-3 mb-4">
        <div className="position-relative flex-grow-1" style={{ maxWidth: '400px' }}>
          <Search className="position-absolute top-50 start-0 translate-middle-y ms-3" size={18} color="#6c757d" />
          <input
            type="text"
            placeholder="Search sellers..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="form-control ps-5"
          />
        </div>
        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          className="form-select w-auto"
        >
          <option value="All">All Status</option>
          <option value="Approved">Approved</option>
          <option value="Pending">Pending</option>
          <option value="Rejected">Rejected</option>
        </select>
      </div>

      {/* Sellers Table */}
      <div className="card shadow-sm">
        <div className="card-body p-0">
          <div className="table-responsive">
            <table className="table table-hover mb-0">
              <thead className="table-light">
                <tr>
                  <th className="small text-muted">Seller</th>
                  <th className="small text-muted">Contact</th>
                  <th className="small text-muted">Products</th>
                  <th className="small text-muted">Total Sales</th>
                  <th className="small text-muted">Status</th>
                  <th className="small text-muted">Join Date</th>
                  <th className="small text-muted">Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredSellers.map((seller) => (
                  <tr key={seller.id}>
                    <td>
                      <div className="d-flex align-items-center gap-3">
                        <div className="bg-light rounded d-flex align-items-center justify-content-center" style={{ width: '40px', height: '40px' }}>
                          <Store size={20} className="text-primary" />
                        </div>
                        <span className="fw-medium">{seller.name}</span>
                      </div>
                    </td>
                    <td>
                      <div className="small">
                        <div className="d-flex align-items-center gap-1 text-muted">
                          <Mail size={14} /> {seller.email}
                        </div>
                        <div className="d-flex align-items-center gap-1 text-muted mt-1">
                          <Phone size={14} /> {seller.phone}
                        </div>
                      </div>
                    </td>
                    <td>{seller.products || 0}</td>
                    <td>₹{(seller.sales || 0).toLocaleString()}</td>
                    <td>{getStatusBadge(seller.status)}</td>
                    <td>{seller.joinDate}</td>
                    <td>
                      <div className="d-flex gap-1">
                        {seller.status === "Pending" && (
                          <>
                            <button 
                              onClick={() => handleApprove(seller.id)}
                              className="btn btn-sm btn-success p-1"
                              title="Approve"
                            >
                              <Check size={16} />
                            </button>
                            <button 
                              onClick={() => handleReject(seller.id)}
                              className="btn btn-sm btn-danger p-1"
                              title="Reject"
                            >
                              <X size={16} />
                            </button>
                          </>
                        )}
                        <button className="btn btn-sm btn-outline-secondary p-1" title="View Details">
                          <Eye size={16} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  )
}

export default SellersPage